import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import {
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import NavLogoFooter from "../../assets/images/logo/NavLogo_footer_white.svg";
import NavLogoDefault from "../../assets/images/logo/B-T-HTMATE.svg";
import VoiceAssistant from "../../components/VoiceAssistant/VoiceAssistant";

const steps = ["Phone Verification", "Patient Details"];

const formatTimer = (seconds) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const decodeEmbeddedParams = (encodedValue) => {
  try {
    if (!encodedValue) return null;
    const base64String = atob(decodeURIComponent(encodedValue));
    const byteArray = Uint8Array.from(base64String, (char) =>
      char.charCodeAt(0)
    );
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(byteArray);
    return JSON.parse(jsonString);
  } catch (err) {
    console.error("Failed to decode registration QR payload", err);
    return null;
  }
};

const buildRegistrationHeaders = (
  appMode,
  orgCode,
  facilityName,
  authToken
) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (appMode) {
    headers["APP-MODE"] = appMode;
  }
  if (orgCode) {
    headers["ORG-CODE"] = orgCode;
  }
  if (facilityName) {
    headers["FACILITY"] = facilityName;
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

const inferImageMimeFromBase64 = (base64 = "") => {
  const trimmed = (base64 || "").trim();
  // Common base64 signatures:
  // - JPEG: /9j
  // - PNG: iVBOR
  // - GIF: R0lGOD
  if (trimmed.startsWith("/9j")) return "image/jpeg";
  if (trimmed.startsWith("iVBOR")) return "image/png";
  if (trimmed.startsWith("R0lGOD")) return "image/gif";
  return "image/png";
};

function PatientRegistrationWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState(["", "", "", "", "", ""]);
  const [authToken, setAuthToken] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpTimer, setOtpTimer] = useState(60);
  const [orgCode, setOrgCode] = useState("");
  const [appMode, setAppMode] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState("");
  const [navLogoSrc, setNavLogoSrc] = useState(NavLogoDefault);
  const [showVoiceAssistant, setShowVoiceAssistant] = useState(false);
  const [registeredPatientId, setRegisteredPatientId] = useState(null);
  const [patientUsername, setPatientUsername] = useState("");
  const [patientPassword, setPatientPassword] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      country: "India",
      gender: "",
      state: "",
      district: "",
      insuranceScheme: [],
      patientName: "",
      username: "",
      password: "",
    },
  });

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactOtp = useMediaQuery("(max-width:430px)");
  const isOtpDisabled = !otpSent;
  const headerStyle = {
    fontSize: isMobile ? "20px" : "28px",
    fontWeight: 600,
    color: "#0d1b3d",
  };
  const primaryControlHeight = isMobile ? "46px" : "52px";
  const watchedPhoneNumber = watch("phoneNumber");
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  // Intentionally run once on mount: reads URL params and fetches facility branding for this session.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    setAuthToken(token);

    // Extract query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const decodedPayload = decodeEmbeddedParams(urlParams.get("data"));

    const orgCodeParam = decodedPayload?.org_code || urlParams.get("org_code");
    const appModeParam = decodedPayload?.app_mode || urlParams.get("app_mode");
    const facilityParam =
      decodedPayload?.facility || urlParams.get("facility");
    if (orgCodeParam) setOrgCode(orgCodeParam);
    if (appModeParam) setAppMode(appModeParam);
    if (facilityParam) {
      const normalizedFacility = facilityParam.replace(/\+/g, " ");
      setFacilityName(normalizedFacility);
    }

    if (orgCodeParam && appModeParam) {
      fetchFacilityBrandConfiguration(appModeParam, orgCodeParam);
    }
  }, []);


  const fetchFacilityBrandConfiguration = async (appModeVal, orgCodeVal) => {
    try {
      console.log("orgCodeParam", orgCodeVal);
      console.log("appModeParam", appModeVal);
      if (orgCodeVal && appModeVal) {
        console.log("fetching facility brand configuration");
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_facility_brand_configuration/`,
          {
            headers: {
              "APP-MODE": appModeVal,
              "ORG-CODE": orgCodeVal,
            },
          }
        );

        if (
          response?.data?.status === "Success" &&
          Array.isArray(response?.data?.data) &&
          response?.data?.data?.[0]?.content
        ) {
          const base64 = String(response.data.data[0].content || "").trim();
          const mime = inferImageMimeFromBase64(base64);
          setNavLogoSrc(`data:${mime};base64,${base64}`);
        }
      }
    } catch (error) {
      // Non-blocking: patient registration should still work without branding.
      console.error("Error fetching facility brand configuration:", error);
    }
  };

  useEffect(() => {
    if (watchedPhoneNumber !== undefined) {
      setValue("phoneNumberConfirm", watchedPhoneNumber);
    }
  }, [watchedPhoneNumber, setValue]);

  useEffect(() => {
    if (activeStep === 1) {
      const phoneToPersist = verifiedPhoneNumber || watchedPhoneNumber;
      if (phoneToPersist) {
        setValue("phoneNumberConfirm", phoneToPersist, {
          shouldValidate: true,
        });
      }
    }
  }, [activeStep, verifiedPhoneNumber, watchedPhoneNumber, setValue]);

  useEffect(() => {
    if (!otpSent || activeStep !== 0 || otpTimer <= 0) return;

    const timerId = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [otpSent, activeStep, otpTimer]);

  useEffect(() => {
    if (otpSent && activeStep === 0 && otpTimer === 0) {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
  }, [otpTimer, otpSent, activeStep]);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const phoneNumber = getValues("phoneNumber");
    if (!phoneNumber) {
      toast.error("Please enter phone number");
      return;
    }

    try {
      const payload = {
        mobile_no: phoneNumber,
      };

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        return;
      }

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}register_patient_mobile_no/`,
        JSON.stringify(payload),
        {
          headers: buildRegistrationHeaders(
            appMode,
            orgCode,
          ),
        }
      );

      if (response?.data?.status === "Success") {
        toast.success("OTP sent successfully!");
        setOtpSent(true);
        setOtpError(false);
        setOtpTimer(60);
        setOtpValue(["", "", "", "", "", ""]);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(
        error?.response?.data?.message ||
        "Unable to send OTP. Please try again."
      );
    }
  };

  const verifyOtp = async (otpCode) => {
    if (!otpSent || otpCode.length !== 6) return;

    const phoneNumber = getValues("phoneNumber");
    if (!phoneNumber) {
      toast.error("Phone number is required before verifying OTP.");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(false);

    try {
      const payload = {
        mobile_no: phoneNumber,
        otp: otpCode,
      };

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        setIsVerifyingOtp(false);
        return;
      }

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}verify_patient_mobile_no/`,
        JSON.stringify(payload),
        {
          headers: buildRegistrationHeaders(
            appMode,
            orgCode,
          ),
        }
      );

      const statusText = response?.data?.status?.toLowerCase();
      const isVerified =
        statusText === "success" && response?.data?.is_valid === true;

      if (isVerified) {
        toast.success("OTP verified successfully!");
        setActiveStep(1);
        setOtpTimer(0);
        setVerifiedPhoneNumber(phoneNumber);
        setValue("phoneNumberConfirm", phoneNumber, { shouldValidate: true });
      } else {
        setOtpError(true);
        toast.error(
          response?.data?.message || "Invalid OTP. Please try again."
        );
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(true);
      toast.error(
        error?.response?.data?.message ||
        "Unable to verify OTP. Please try again."
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (isVerifyingOtp) return;

    const digit = value.replace(/\D/g, "");
    if (digit.length > 1) return; // Only allow single digit

    const newOtp = [...otpValue];
    newOtp[index] = digit;
    setOtpValue(newOtp);
    setOtpError(false);

    // Auto-focus next input
    if (digit && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    const enteredOtp = newOtp.join("");
    const isOtpComplete = newOtp.every((char) => char !== "");
    if (isOtpComplete && enteredOtp.length === 6) {
      verifyOtp(enteredOtp);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValue[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const onSubmitPatient = async (data) => {
    console.log("Patient data:", data);

    try {
      const dateParts = data.dateOfBirth.split("-");
      const facilityDisplayName =
        facilityName || sessionStorage.getItem("facility_display_name") || "";

      const formattedData = {
        facilityName: facilityDisplayName,
        patientName: data.patientName,
        gender: data.gender,
        locality: data.locality,
        address: data.locality, // Using locality as address if address field is not separate
        city: data.city || "",
        pincode: data.pincode,
        phoneNumber: data.phoneNumberConfirm || watchedPhoneNumber,
        emailId: data.emailId || "",
        yearOfBirth: dateParts[0],
        monthOfBirth: dateParts[1],
        dayOfBirth: dateParts[2],
        username: data.username,
        password: data.password,
      };

      console.log("Formatted patient data for submission:", formattedData);

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        return;
      }

      const headers = {
        ...buildRegistrationHeaders(
          appMode,
          orgCode,
        ),
      };

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}register_patient/`,
        JSON.stringify(formattedData),
        {
          headers,
        }
      );

      // if (response?.data?.message === "Patient created successfully !!") {
      //   const patientId = response?.data?.patient?.patient_id;
      if (response?.data?.status === "Success") {
        const patientId = response?.data?.data?.patient_id;

        toast.success(
          `Patient registered successfully! Patient ID: ${patientId}`
        );

        // Show voice assistant after successful registration
        // setRegisteredPatientId(patientId);//(triaging commented for now)
        // setShowVoiceAssistant(true);//(triaging commented for now)
        // setPatientUsername(data?.username);//(triaging commented for now)
        // setPatientPassword(data?.password);//(triaging commented for now)

        // Reset form and go back to first step (optional - you may want to keep this commented)
        // reset();
        // setOtpSent(false);
        // setOtpValue(["", "", "", "", "", ""]);
        // setOtpError(false);
        // setIsVerifyingOtp(false);
        // setOtpTimer(0);
        // setActiveStep(0);
      } else {
        toast.error(
          response?.data?.message || response?.data?.error ||
          "Failed to register patient. Please try again."
        );
      }
    } catch (error) {
      console.error("Error registering patient:", error);
      toast.error(
        error?.response?.data?.message ||
        "Unable to register patient. Please try again."
      );
    }
  };

  const renderErrorMessage = (error) =>
    error ? (
      <p className="errorMsg" style={{ color: "red", marginTop: "6px" }}>
        {error.message}
      </p>
    ) : null;

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        const otpBoxSize = isCompactOtp ? 44 : isMobile ? 48 : 60;
        const otpFontSize = isCompactOtp ? "18px" : isMobile ? "20px" : "24px";
        const otpGap = isCompactOtp ? "3px" : "12px";
        const otpInputStyle = {
          width: `${otpBoxSize}px`,
          height: `${otpBoxSize}px`,
          textAlign: "center",
          fontSize: otpFontSize,
          fontWeight: "bold",
          color: "#0d1b3d",
          caretColor: "#0d1b3d",
          WebkitTextFillColor: "#0d1b3d",
          lineHeight: `${otpBoxSize - 4}px`,
          padding: "0",
          fontFamily: "'Inter', 'Roboto Mono', monospace",
          border: `2px solid ${otpError ? "#d32f2f" : isOtpDisabled ? "#bdbdbd" : "#1976d2"
            }`,
          borderRadius: "8px",
          backgroundColor: otpError
            ? "#ffebee"
            : isOtpDisabled
              ? "#f5f5f5"
              : "#fff",
          transition: "border-color 0.2s",
          outline: "none",
          boxSizing: "border-box",
        };
        return (
          <div className="form-section mt-3">
            <h4 className="section-title mb-3" style={headerStyle}>
              Phone Verification
            </h4>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label
                    htmlFor="phoneNumber"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Phone Number <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    className={`form-control ${errors.phoneNumber ? "is-invalid" : ""
                      }`}
                    placeholder="Enter 10-digit phone number"
                    disabled={otpSent}
                    style={{ height: primaryControlHeight }}
                    maxLength={10}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    }}
                    {...register("phoneNumber", {
                      required: "Phone Number is required.",
                      minLength: {
                        value: 10,
                        message: "Phone Number must be exactly 10 digits.",
                      },
                      maxLength: {
                        value: 10,
                        message: "Phone Number must be exactly 10 digits.",
                      },
                      pattern: {
                        value: /^[6-9][0-9]{9}$/,
                        message:
                          "Phone Number must start with 6, 7, 8, or 9 and be 10 digits.",
                      },
                      validate: {
                        onlyNumbers: (value) => {
                          if (!value) return true;
                          return (
                            /^[0-9]+$/.test(value) ||
                            "Phone Number can only contain numbers."
                          );
                        },
                        validLength: (value) => {
                          if (!value) return true;
                          return (
                            value.length === 10 ||
                            "Phone Number must be exactly 10 digits."
                          );
                        },
                      },
                    })}
                  />
                  {renderErrorMessage(errors.phoneNumber)}
                </div>
              </div>
              <div className="col-12 col-md-6 mt-4">
                <button
                  type="button"
                  className="btn btn__secondary submit-details-btn w-100 mt-3 mt-md-0"
                  onClick={handleSendOtp}
                  disabled={otpSent}
                  style={{ height: primaryControlHeight }}
                >
                  {otpSent ? "OTP Sent" : "Send OTP"}
                </button>
              </div>
            </div>

            <div className="otp-section mt-4 text-center">
              <p className="fw-semibold mb-1">Enter 6-digit OTP</p>
              {isOtpDisabled && (
                <p className="text-muted mb-1">
                  Send OTP to enable this section.
                </p>
              )}
              {otpSent && otpTimer > 0 && (
                <p className="text-muted mb-1">
                  Time remaining: {formatTimer(otpTimer)}
                </p>
              )}
              {isVerifyingOtp && (
                <p className="text-muted mb-1">Verifying OTP...</p>
              )}
              {otpError && (
                <p style={{ color: "#d32f2f" }}>
                  Invalid OTP. Please try again.
                </p>
              )}

              <div
                className="d-flex justify-content-center flex-wrap mt-3"
                style={{
                  opacity: isOtpDisabled ? 0.7 : 1,
                  columnGap: otpGap,
                  rowGap: otpGap,
                }}
              >
                {otpValue.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    className="form-control otp-input"
                    value={digit}
                    disabled={isOtpDisabled || isVerifyingOtp}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    maxLength={1}
                    style={otpInputStyle}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="form-section mt-3">
            <h4 className="section-title mb-3" style={headerStyle}>
              Patient Details
            </h4>
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="patientName"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="patientName"
                    type="text"
                    className={`form-control ${errors.patientName ? "is-invalid" : ""
                      }`}
                    aria-label="Patient Name"
                    autoComplete="new-password"
                    {...register("patientName")}
                  />
                  {renderErrorMessage(errors.patientName)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="phoneNumberDisplay"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Phone Number <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="hidden"
                    {...register("phoneNumberConfirm", {
                      required: "Phone Number is required.",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Phone Number must be 10 digits.",
                      },
                    })}
                  />
                  <input
                    id="phoneNumberDisplay"
                    type="tel"
                    className="form-control bg-light"
                    value={verifiedPhoneNumber || watchedPhoneNumber || ""}
                    aria-label="Verified Phone Number"
                    readOnly
                    tabIndex={-1}
                    style={{ pointerEvents: "none" }}
                  />
                  {renderErrorMessage(errors.phoneNumberConfirm)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "Gender is required." }}
                  render={({ field }) => (
                    <div className="form-group">
                      <label
                        htmlFor="gender"
                        className="form-label"
                        style={{ display: "flex", alignItems: "start" }}
                      >
                        Gender <span style={{ color: "red" }}> *</span>
                      </label>
                      <select
                        id="gender"
                        className={`form-control ${errors.gender ? "is-invalid" : ""
                          }`}
                        {...field}
                        value={field.value || ""}
                        aria-label="Gender"
                      >
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="other">Others</option>
                      </select>
                      {renderErrorMessage(errors.gender)}
                    </div>
                  )}
                />
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="dateOfBirth"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Date of Birth <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="dateOfBirth"
                    type="date"
                    className={`form-control ${errors.dateOfBirth ? "is-invalid" : ""
                      }`}
                    max={today}
                    aria-label="Date of Birth"
                    {...register("dateOfBirth", {
                      required: "Date of Birth is required.",
                      validate: {
                        notFuture: (value) =>
                          value <= today ||
                          "Date of Birth cannot be in the future.",
                        validDate: (value) => {
                          if (!value) return true;
                          const date = new Date(value);
                          const minDate = new Date("1900-01-01");
                          return (
                            date >= minDate ||
                            "Date of Birth must be after 1900."
                          );
                        },
                      },
                    })}
                  />
                  {renderErrorMessage(errors.dateOfBirth)}
                </div>
              </div>

              <input type="hidden" {...register("country")} />
              <Controller
                name="state"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="district"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="locality"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Address <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="locality"
                    type="text"
                    className={`form-control ${errors.locality ? "is-invalid" : ""
                      }`}
                    aria-label="Address"
                    {...register("locality", {
                      required: "Address is required.",
                      minLength: {
                        value: 5,
                        message: "Address must be at least 5 characters long.",
                      },
                      maxLength: {
                        value: 200,
                        message: "Address must not exceed 200 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.locality)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="city"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    City / Village <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    className={`form-control ${errors.city ? "is-invalid" : ""
                      }`}
                    aria-label="City or Village"
                    {...register("city", {
                      required: "City / Village is required.",
                      minLength: {
                        value: 2,
                        message:
                          "City / Village must be at least 2 characters long.",
                      },
                      maxLength: {
                        value: 100,
                        message:
                          "City / Village must not exceed 100 characters.",
                      },
                      pattern: {
                        value: /^[a-zA-Z\s'-]+$/,
                        message:
                          "City / Village can only contain letters, spaces, hyphens, and apostrophes.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.city)}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label
                    htmlFor="pincode"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Pin Code <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="pincode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={`form-control ${errors.pincode ? "is-invalid" : ""
                      }`}
                    aria-label="Pin Code"
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^0-9]/g, "")
                        .slice(0, 6);
                    }}
                    {...register("pincode", {
                      required: "Pin Code is required.",
                      minLength: {
                        value: 6,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                      maxLength: {
                        value: 6,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.pincode)}
                </div>
              </div>
              {/* //(triaging commented for now) */}
              {/* <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="username"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Username <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    className={`form-control ${errors.username ? "is-invalid" : ""
                      }`}
                    aria-label="Username"
                    autoComplete="username"
                    placeholder="Enter username"
                    {...register("username", {
                      required: "Username is required.",
                      minLength: {
                        value: 2,
                        message: "Username must be at least 2 characters long.",
                      },
                      maxLength: {
                        value: 100,
                        message: "Username must not exceed 100 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.username)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="password"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Password <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    className={`form-control ${errors.password ? "is-invalid" : ""
                      }`}
                    aria-label="Password"
                    autoComplete="new-password"
                    placeholder="Enter password"
                    {...register("password", {
                      required: "Password is required.",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters long.",
                      },
                      maxLength: {
                        value: 128,
                        message: "Password must not exceed 128 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.password)}
                </div>
              </div> */}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="wrapper"
      style={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <nav
        className="navbar navbar-expand-lg sticky-navbar"
        style={{ flex: "0 0 auto" }}
      >
        <div className="container-fluid" style={{ justifyContent: "center" }}>
          {
            <div
              className="navbar-brand"
              style={{
                textAlign: "right",
              }}
            >
              <img
                src={navLogoSrc}
                className="logo-dark logo-img"
                alt="logo"
                style={{
                  height: isSmallScreen ? "52px" : "72px",
                  maxWidth: "100%",
                  objectFit: "contain",
                  paddingBottom: "3px",
                  paddingTop: "5px",
                  marginLeft: isSmallScreen ? "12px" : "30px",
                }}
              />
            </div>
          }
          <div
            style={{
              display: "flex",
              flexDirection: "row",
            }}
          ></div>

          <div style={{ display: "flex" }}>
            <div className="d-none d-xl-flex" style={{ marginRight: 16 }}>
              <Box display="flex" alignItems="stretch">
                {sessionStorage.getItem("accessToken") !== undefined &&
                  sessionStorage.getItem("accessToken") !== null ? (
                  <>
                    <Grid
                      container
                      direction="column"
                      spacing={0.5}
                      style={{ textAlign: "right" }}
                      justifyContent="center"
                    ></Grid>
                  </>
                ) : null}
              </Box>
            </div>
          </div>
        </div>
      </nav>
      <div
        className="container my-4 patient-registration-wrapper"
        style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}
      >
        {showVoiceAssistant ? (
          <VoiceAssistant
            // patientId={registeredPatientId}
            patient={{ patientId: registeredPatientId, patientUsername: patientUsername, patientPassword: patientPassword }}
            orgCode={orgCode}
            appMode={appMode}
            onClose={() => {
              setShowVoiceAssistant(false);
              // Optionally reset the form after voice assistant is closed
              reset();
              setOtpSent(false);
              setOtpValue(["", "", "", "", "", ""]);
              setOtpError(false);
              setIsVerifyingOtp(false);
              setOtpTimer(0);
              setActiveStep(0);
              setRegisteredPatientId(null);
            }}
          />
        ) : (
          <div
            className="registration-card mx-auto"
            style={{
              maxWidth: isMobile ? "100%" : "900px",
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "32px",
              boxShadow: "0 12px 30px rgba(23, 43, 77, 0.08)",
            }}
          >
            <h4 className="text-center mb-4" style={headerStyle}>
              Patient Registration
            </h4>

            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              alternativeLabel
              sx={{ mb: isMobile ? 3 : 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <form onSubmit={handleSubmit(onSubmitPatient)} autoComplete="off">
              {renderStepContent(activeStep)}

              {activeStep === steps.length - 1 && (
                <div
                  className={`pt-3 mt-4 d-flex ${isMobile ? "w-100" : "justify-content-center"
                    }`}
                >
                  <button
                    type="submit"
                    className={`btn btn__secondary mt-6 submit-details-btn ${isMobile ? "w-100" : ""
                      }`}
                    style={isMobile ? {} : { minWidth: "220px" }}
                  >
                    Submit
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
      <div className="footer" style={{ flex: "0 0 auto" }}>
        <Box
          className="footer-primary"
          sx={{
            px: { xs: 2, sm: 3 }, // responsive left/right padding
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            {/* <Grid item xs={12} md={4}>
              
            </Grid> */}

            <Grid item xs={12} md={12}>
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#F4F4F4" }}
              >
                Powered by
                {/* HlthTek Optimus Pvt. Ltd. */}
                <img
                  src={NavLogoFooter}
                  className="logo-dark logo-img"
                  alt="logo"
                  height={isSmallScreen ? "26px" : "32px"}
                  style={{
                    marginLeft: "5px",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Typography>
            </Grid>

            {/* <Grid item xs={12} md={4}></Grid> */}
          </Grid>
        </Box>
      </div>
    </div>
  );
}

export default PatientRegistrationWizard;
